import { useState, useEffect } from "react";
import { Link, useNavigate, useSearchParams } from "react-router-dom";
import { toast } from "sonner";
import { useAuth } from "../lib/auth";
import { Hourglass, Eye, EyeOff } from "lucide-react";

export default function Auth({ mode = "login" }) {
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [name, setName] = useState("");
  const [busy, setBusy] = useState(false);
  const [showPw, setShowPw] = useState(false);
  const { login, signup } = useAuth();

  // REMINDER: DO NOT HARDCODE THE URL, OR ADD ANY FALLBACKS OR REDIRECT URLS, THIS BREAKS THE AUTH
  const continueWithGoogle = () => {
    const redirectUrl = window.location.origin + "/dashboard";
    window.location.href = `https://auth.emergentagent.com/?redirect=${encodeURIComponent(redirectUrl)}`;
  };
  const nav = useNavigate();
  const [params] = useSearchParams();
  const plan = params.get("plan");

  // Capture affiliate referral code from URL on signup page
  useEffect(() => {
    const ref = params.get("ref");
    if (ref) {
      sessionStorage.setItem("affiliate_ref", ref.trim().toUpperCase());
    }
  }, [params]);

  const onSubmit = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      if (mode === "login") await login(email, password);
      else {
        const ref = sessionStorage.getItem("affiliate_ref") || undefined;
        await signup(email, password, name, ref);
      }
      toast.success(mode === "login" ? "Welcome back" : "Account created. Now claim your seat.");
      if (plan) nav(`/pricing?auto=${plan}`); else nav("/dashboard");
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Something went wrong");
    } finally { setBusy(false); }
  };

  return (
    <div className="min-h-screen flex items-center justify-center px-6">
      <div className="w-full max-w-md">
        <div className="flex items-center gap-3 mb-8">
          <Hourglass className="w-5 h-5 text-gold animate-glow" />
          <div className="overline">// NOWCOMMAND</div>
        </div>
        <h1 className="font-display text-5xl text-cream leading-none mb-3">{mode === "login" ? "Re-enter" : "Cross over"}</h1>
        <p className="text-textMuted mb-4">{mode === "login" ? "Sign back into the realm." : "Create your seat. Stripe checkout is the next step."}</p>
        {plan && (
          <div className="mb-6 rounded border border-gold/20 bg-gold/5 p-4 text-sm text-cream">
            {mode === "login"
              ? "After signing in, you'll be redirected to Stripe checkout to complete your purchase."
              : "After creating your account, you'll be sent straight into Stripe checkout to complete your purchase."}
          </div>
        )}
        <form onSubmit={onSubmit} className="space-y-6">
          {mode === "signup" && (
            <div>
              <label className="overline">// NAME</label>
              <input data-testid="auth-name" value={name} onChange={(e) => setName(e.target.value)} placeholder="Your sovereign name" required />
            </div>
          )}
          <div>
            <label className="overline">// EMAIL</label>
            <input data-testid="auth-email" type="email" value={email} onChange={(e) => setEmail(e.target.value)} placeholder="you@dominion.com" required />
          </div>
          <div>
            <label className="overline">// PASSWORD</label>
            <div className="relative">
              <input data-testid="auth-password" type={showPw ? "text" : "password"} value={password} onChange={(e) => setPassword(e.target.value)} placeholder="••••••••" minLength={6} required className="pr-12" />
              <button type="button" data-testid="toggle-password" onClick={() => setShowPw(!showPw)} aria-label={showPw ? "Hide password" : "Show password"}
                className="absolute right-3 top-1/2 -translate-y-1/2 text-textMuted hover:text-gold transition-colors">
                {showPw ? <EyeOff className="w-5 h-5" /> : <Eye className="w-5 h-5" />}
              </button>
            </div>
          </div>
          <button data-testid="auth-submit" type="submit" disabled={busy} className="btn-gold w-full justify-center disabled:opacity-60">
            {busy ? "Working..." : mode === "login" ? "Sign in" : "Create my seat"}
          </button>
        </form>

        <div className="flex items-center gap-4 my-6">
          <div className="h-px flex-1 bg-borderGold" />
          <span className="overline">// OR</span>
          <div className="h-px flex-1 bg-borderGold" />
        </div>
        {/* REMINDER: DO NOT HARDCODE THE URL, OR ADD ANY FALLBACKS OR REDIRECT URLS, THIS BREAKS THE AUTH */}
        <button data-testid="google-signin" type="button" onClick={continueWithGoogle}
          className="w-full flex items-center justify-center gap-3 border border-borderGold bg-surface/60 hover:bg-surface text-cream py-3 transition-colors">
          <svg width="18" height="18" viewBox="0 0 48 48" aria-hidden="true"><path fill="#FFC107" d="M43.6 20.5H42V20H24v8h11.3C33.7 32.4 29.3 35 24 35c-6.1 0-11-4.9-11-11s4.9-11 11-11c2.8 0 5.4 1.1 7.3 2.8l5.7-5.7C33.6 6.1 29 4 24 4 12.9 4 4 12.9 4 24s8.9 20 20 20 20-8.9 20-20c0-1.2-.1-2.3-.4-3.5z"/><path fill="#FF3D00" d="M6.3 14.7l6.6 4.8C14.7 16 19 13 24 13c2.8 0 5.4 1.1 7.3 2.8l5.7-5.7C33.6 6.1 29 4 24 4 16.3 4 9.7 8.3 6.3 14.7z"/><path fill="#4CAF50" d="M24 44c5.2 0 9.9-2 13.4-5.2l-6.2-5.2C29.2 34.9 26.7 36 24 36c-5.3 0-9.7-2.6-11.3-7l-6.5 5C9.6 39.6 16.2 44 24 44z"/><path fill="#1976D2" d="M43.6 20.5H42V20H24v8h11.3c-.8 2.2-2.2 4.1-4.1 5.6l6.2 5.2C39.9 36.5 44 30.8 44 24c0-1.2-.1-2.3-.4-3.5z"/></svg>
          Continue with Google
        </button>
        <div className="mt-8 text-sm text-textMuted">
          {mode === "login" ? (
            <>No account? <Link to="/signup" data-testid="auth-switch" className="text-gold hover:underline">Cross over</Link></>
          ) : (
            <>Already a member? <Link to="/login" data-testid="auth-switch" className="text-gold hover:underline">Sign in</Link></>
          )}
        </div>
      </div>
    </div>
  );
}
